import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICellRendererParams } from 'ag-grid-community';
import { MatBadge } from '@angular/material/badge';

export interface IconParams  {
  icon: string;
  color?: string;
  tooltip: string | ((rowData: any) => string);
  condition: any;
  loading?: (rowData: any) => boolean;
  onClick?: (rowData: any) => void;
  badge?: string | ((rowData: any) => string | null);
  badgeColorClass?: string | ((rowData: any) => string);
}

export interface IconCellRendererParams extends ICellRendererParams {
  iconParams: IconParams[];
  useValue?: boolean;
}

@Component({
  selector: 'app-icon-cell-renderer-component',
  imports: [MatIconModule, MatTooltipModule, MatButtonModule, CommonModule, MatBadge],
  templateUrl: './icon-cell-renderer-component.html',
  styleUrl: './icon-cell-renderer-component.css'
})
export class IconCellRendererComponent {
  public readonly params = signal<IconCellRendererParams | null>(null);
  public readonly rowData = signal<any>(null);

  public readonly visibleIcon = computed<IconParams | undefined>(() => {
    const params = this.params();
    const rowData = this.rowData();

    if (!params || !rowData) {
      return undefined;
    }

    return params.iconParams.find(param => param.condition(rowData));
  });

  public readonly animateLoading = computed<boolean>(() => {
    const params = this.params();
    const visibleIcon = this.visibleIcon();

    if (!params || !visibleIcon || !visibleIcon.loading) {
      return false;
    }

    return visibleIcon.loading(params);
  });

  public readonly tooltipText = computed<string>(() => {
    const visibleIcon = this.visibleIcon();
    const params = this.params();
    
    if (!visibleIcon) return '';

    if (typeof visibleIcon.tooltip === 'function') {
      return visibleIcon.tooltip(params?.data);
    }

    return visibleIcon.tooltip;
  });

  public readonly badge = computed<string | null>(() => {
    const visibleIcon = this.visibleIcon();
    const params = this.params();

    if (!visibleIcon || !visibleIcon.badge) {
      return null;
    }

    if (typeof visibleIcon.badge === 'function') {
      return visibleIcon.badge(params?.data);
    }

    return visibleIcon.badge;
  });

  public readonly badgeColorClass = computed<string>(() => {
    const visibleIcon = this.visibleIcon();
    const params = this.params();

    if (!visibleIcon || !visibleIcon.badgeColorClass) {
      return '';
    }

    if (typeof visibleIcon.badgeColorClass === 'function') {
      return visibleIcon.badgeColorClass(params?.data);
    }

    return visibleIcon.badgeColorClass;
  });

  agInit(params: IconCellRendererParams): void {
    this.refresh(params);
  }

  refresh(params: IconCellRendererParams): boolean {
    this.params.set(params);
    this.rowData.set(params.useValue ? params.value : params.data);
    return true;
  }

  onClick(event: Event): void {
    event.stopPropagation();

    const visibleIcon = this.visibleIcon();
    if (visibleIcon && visibleIcon.onClick) {
      visibleIcon.onClick(this.rowData());
    }
  }
}
